import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import { formatBytes } from "utils/common";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { isRequiredAssetField, prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { PINTEREST_VIDEO_CONSTRAINTS } from "social/validators/pinterest";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import PinterestDisclosure from "social/PinterestDisclosure";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";
import { afterEffectsPackage } from "social/AfterEffectsPackageSelector";
import { afterEffectsComposition } from "social/AfterEffectsCompSelector";
import { frameForThumbnail } from "social/FrameForThumbnail";

const {
	MAX_LENGTH_TITLE,
	MAX_LENGTH_DESCRIPTION,
	MAX_LENGTH_ALT_TEXT,
	MAX_LENGTH_LANDING_PAGE,
	VIDEO_ACCEPT,
	MAX_VIDEO_BYTES,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	THUMBNAIL_ACCEPT,
	MAX_THUMBNAIL_BYTES,
} = PINTEREST_VIDEO_CONSTRAINTS;

const PINTEREST_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${formatBytes(MAX_THUMBNAIL_BYTES)}`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	}
];

export const PINTEREST_VIDEO_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	afterEffectsPackage,
	afterEffectsComposition,
	video: {
		name: "video",
		label: "Video",
		component: DynamicAssetSelect,
		defaultDynVarName: "video",
		assetType: ASSET_TYPES.VIDEO,
		required: true,
		className: "full-width",
		accept: VIDEO_ACCEPT,
		tip: {
			direction: "top",
			description: "Pinterest recommends making your videos square (1:1) or vertical (2:3 or 9:16)",
		},
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum`,
			},
			{
				name: "File Types",
				description: VIDEO_ACCEPT,
			},
			{
				name: "Dimensions",
				description: "Square (1:1) or vertical (2:3 or 9:16)",
			},
			{
				name: "Duration",
				description: `Between ${MIN_VIDEO_SEC} sec and ${MAX_VIDEO_MIN} min`,
			}
		],
	},
	thumbnail: {
		name: "thumbnail",
		label: "Thumbnail",
		className: "full-width",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: "video_thumbnail",
		requirements: PINTEREST_THUMBNAIL_REQUIREMENTS,
		accept: THUMBNAIL_ACCEPT,
		tip: {
			direction: "top",
			description: "Video and thumbnail image must have the same aspect ratio",
		},
	},
	frameForThumbnail,
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
		className: "full-width",
	},
	title: {
		name: "title",
		label: "Title",
		placeholder: "Enter a short headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		charMax: MAX_LENGTH_TITLE,
		// showCharCount: true,
		className: "full-width",
		tip: {
			direction: "right",
			description: "May get truncated after 40 characters",
		},
	},
	description: {
		name: "description",
		label: "Description",
		placeholder: "Tell people what your creative is about",
		component: DynamicInput,
		defaultDynVarName: "description",
		charMax: MAX_LENGTH_DESCRIPTION,
		// showCharCount: true,
		className: "full-width",
		tip: {
			direction: "right",
			description: `Descriptions are used by the Pinterest algorithm to determine relevance for delivery,
			do not appear when viewing the Pin in the home or search feed, and do not appear for ads when viewed up close.`,
			size: 'xlarge',
		},
	},
	altText: {
		name: "altText",
		label: "Alt Text",
		placeholder: "Text for screen readers...",
		component: DynamicInput,
		defaultDynVarName: "alt_text",
		charMax: MAX_LENGTH_ALT_TEXT,
		className: "full-width",
		tip: {
			direction: "right",
			description: `Explain what people can see in the pin. This text will be read aloud by screen readers.`,
			size: 'xlarge',
		},
	},
	disclosure: {
		name: "disclosureEnabled",
		component: PinterestDisclosure,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		required: true,
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
	},
};

const adType = SOCIAL_AD_TYPES.PINTEREST_VIDEO;

export default function PinterestVideo(props){
	const {
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
		isEditMode,
	} = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, PINTEREST_VIDEO_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(PINTEREST_VIDEO_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "video") {
			extraProps.required = isRequiredAssetField(assetType);
		}

		if (fieldProps.name === "video" || fieldProps.name === "thumbnail") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
		}

		if(fieldProps.name === "frameNumber"){
			extraProps.isEditMode = isEditMode;
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

PinterestVideo.displayName = "PinterestVideoForm";
